// ─────────────────────────────────────────────
//  useTrackGeometry — measure where a room's track actually starts and how wide
//  it is, so the chart's overlays stop guessing.
//
//  ⚠️ A CALLBACK REF, NOT `useEffect(…, [])`. The chart lives inside the
//  `occupiedCount === 0 ? empty : chart` branch, so on first render the node does
//  not exist. An effect keyed on `[]` runs once, finds nothing, and measures
//  nothing — silently, forever. (The axis-label measurement on this page already
//  carries this scar; this is the same shape.)
//
//  The track node is found by attribute rather than by a second ref: every room
//  row carries `data-tl-track`, they are all the same geometry, and the first one
//  is as good as any. That also keeps the page from having to thread a ref
//  through two `.map()` levels to reach one arbitrary row.
// ─────────────────────────────────────────────
import { useCallback, useEffect, useRef, useState } from "react";
import { trackOffsetPx } from "../../domain/timelineGeometry";

export default function useTrackGeometry() {
  // { offsetPx, trackWidth } — or null until something has laid out. Null is the
  // signal to keep the old fallback rather than to draw an overlay at zero.
  const [geom, setGeom] = useState(null);
  const observerRef = useRef(null);

  const measure = useCallback((chartEl) => {
    if (!chartEl) return;
    const trackEl = chartEl.querySelector("[data-tl-track]");
    if (!trackEl) return;
    const trackRect = trackEl.getBoundingClientRect();
    const offsetPx = trackOffsetPx({
      chartRect: chartEl.getBoundingClientRect(),
      // `left` on an absolutely-positioned child is measured from the padding
      // box; a bounding rect includes the border. `clientLeft` IS that border.
      chartBorderLeft: chartEl.clientLeft || 0,
      trackRect,
    });
    if (offsetPx == null) return; // jsdom / not laid out — keep the fallback
    setGeom((prev) =>
      prev && prev.offsetPx === offsetPx && prev.trackWidth === trackRect.width
        ? prev
        : { offsetPx, trackWidth: trackRect.width }
    );
  }, []);

  const chartRef = useCallback(
    (node) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (!node) return;
      measure(node);
      if (typeof ResizeObserver === "undefined") return; // jsdom
      const ro = new ResizeObserver(() => measure(node));
      ro.observe(node);
      observerRef.current = ro;
    },
    [measure]
  );

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return { chartRef, geom };
}
